import React, { useState, useEffect, useRef } from 'react';
import {
  Typography, Box, TextField, Button, MenuItem, FormControlLabel,
  Checkbox, Avatar, IconButton, Paper, Stepper, Step, StepLabel, CircularProgress,
  StepConnector, styled, Card, CardContent, Tooltip, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, AlertTitle
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createStudent, validateStudentId } from '../slices/studentsSlice';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PhotoCamera, ArrowBack, ArrowForward, Person, School, 
  Contacts, NoteAdd, Check, KeyboardArrowLeft, KeyboardArrowRight,
  Save, Cancel, CalendarMonth, Badge, Wc, Phone, AttachFile,
  Warning
} from '@mui/icons-material';
import { fetchClasses } from '../slices/classesSlice';
import { fetchBuses } from '../slices/busesSlice';

const MotionPaper = motion(Paper);

// Custom styled components for enhanced visual appeal
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.MuiStepConnector-alternativeLabel`]: {
    top: 23,
    // Use logical (RTL-aware) properties so the line clips correctly in both LTR and RTL
    left: 'unset',
    right: 'unset',
    insetInlineStart: 'calc(-50% + 26px)',
    insetInlineEnd: 'calc(50% + 26px)',
  },
  [`&.MuiStepConnector-active`]: {
    [`& .MuiStepConnector-line`]: {
      backgroundImage: 'linear-gradient(95deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
    },
  },
  [`&.MuiStepConnector-completed`]: {
    [`& .MuiStepConnector-line`]: {
      backgroundImage: 'linear-gradient(95deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
    },
  },
  [`& .MuiStepConnector-line`]: {
    height: 4,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#E5E7EB',
    borderRadius: 4,
  },
}));

const PHONE_CODE_OPTIONS = [
  { value: '972', label: '+972', flag: '🇵🇸' },
  { value: '970', label: '+970', flag: '🇵🇸' }
];


const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#E5E7EB',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 6px rgba(67,56,202,0.12)',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
    boxShadow: '0 0 0 4px rgba(67,56,202,0.18), 0 4px 14px rgba(67,56,202,0.4)',
    transform: 'scale(1.12)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #22C55E 0%, #16A34A 100%)',
    boxShadow: '0 3px 10px rgba(22,163,74,0.35)',
  }),
}));

// Custom step icon component
function ColorlibStepIcon(props) {
  const { active, completed, className, icon } = props;
  const icons = {
    1: <Person />,
    2: <School />,
    3: <Contacts />,
    4: <NoteAdd />,
    5: <AttachFile />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {completed ? <Check /> : icons[icon]}
    </ColorlibStepIconRoot>
  );
}

// Animation variants for page transitions
const pageVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 }
};

const steps = ['المعلومات الأساسية', 'المعلومات الأكاديمية', 'بيانات ولي الأمر', 'ملاحظات', 'مرفقات'];

const CreateStudentPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const { list: classes, status: classesStatus } = useSelector((state) => state.classes);
  const { list: buses, status: busesStatus } = useSelector((state) => state.buses);
  const { validationStatus } = useSelector((state) => state.students);

  const [student, setStudent] = useState({
    first_name: '',
    second_name: '',
    gender: '',
    birthdate: '',
    school_class: '',
    class: '',
    parent_name: '',
    parent_phone_code: '972',
    parent_phone_2_code: '972',
    address: '',
    parent_phone: '',
    parent_phone_2: '', // New optional phone field
    parent_email: '',
    date_of_registration: new Date().toISOString().split('T')[0],
    note: '',
    attachment: null,
    attachmentPreview: null,
    student_id: '',
    is_bus_joined: false,
    bus: ''
  });

  useEffect(() => {
    dispatch(fetchClasses());
    dispatch(fetchBuses());
  }, [dispatch]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [triedNext, setTriedNext] = useState(false);
  
  // Add these new state variables for student ID validation
  const [studentIdError, setStudentIdError] = useState('');
  
  // Add state for maximum students dialog
  const [maxStudentsDialog, setMaxStudentsDialog] = useState(false);
  const [maxStudentsMessage, setMaxStudentsMessage] = useState('');
  
  // Add state for additional error details
  const [maxStudentsDetails, setMaxStudentsDetails] = useState(null);
  
  // Debug state changes
  useEffect(() => {
    console.log('Max students dialog state changed:', maxStudentsDialog);
    console.log('Max students message changed:', maxStudentsMessage);
  }, [maxStudentsDialog, maxStudentsMessage]);
  
  // Debug state changes
  useEffect(() => {
    console.log('Max students dialog state changed:', maxStudentsDialog);
    console.log('Max students message changed:', maxStudentsMessage);
  }, [maxStudentsDialog, maxStudentsMessage]);
  
  // Get validation status from Redux
  const studentIdValidating = validationStatus === 'validating';

  // Student ID validation function using Redux thunk
  const handleStudentIdValidation = async (studentId) => {
    if (!studentId || studentId.length < 2) {
      setStudentIdError('');
      return;
    }

    try {
      const result = await dispatch(validateStudentId(studentId)).unwrap();
      
      if (result.isDuplicate) {
        setStudentIdError(`رقم الهوية "${studentId}" موجود بالفعل للطالب ${result.student.first_name} ${result.student.second_name}`);
      } else {
        setStudentIdError('');
      }
    } catch (error) {
      console.error('Error validating student ID:', error);
      setStudentIdError('');
    }
  };

  const handleChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    if (files) {
      const file = files[0];
      const preview = URL.createObjectURL(file);
      setStudent((prev) => ({ ...prev, [name]: file, attachmentPreview: preview }));
    } else {
      setStudent((prev) => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value,
        ...(name === 'is_bus_joined' && !checked && { bus: '' })
      }));
      
      // Clear student ID error when user starts typing
      if (name === 'student_id') {
        setStudentIdError('');
      }
    }
  };

  const isArabic = (str) => /^[\u0600-\u06FF\s]+$/.test(str);
  const isValidId = (id) => /^\d{9}$/.test(id);
  const isValidPhone = (phone) => /^0\d{9}$/.test(phone); // 10 digits, starts with 0
  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validateStep = (step) => {
  const tempErrors = {};

  if (step === 0) {
    if (!student.first_name) tempErrors.first_name = 'الاسم الأول مطلوب';
    else if (!isArabic(student.first_name)) tempErrors.first_name = 'الاسم الأول يجب أن يكون باللغة العربية فقط';

    if (!student.second_name) tempErrors.second_name = 'اسم العائلة مطلوب';
    else if (!isArabic(student.second_name)) tempErrors.second_name = 'اسم العائلة يجب أن يكون باللغة العربية فقط';

    if (!student.gender) tempErrors.gender = 'الجنس مطلوب';
    if (!student.birthdate) tempErrors.birthdate = 'تاريخ الميلاد مطلوب';

    // Update student ID validation to include the live validation error
    if (student.student_id) {
      if (!isValidId(student.student_id)) {
        tempErrors.student_id = 'رقم الهوية يجب أن يتكون من 9 أرقام صحيحة';
      } else if (studentIdError) {
        tempErrors.student_id = studentIdError;
      }
    }
  } else if (step === 1) {
    if (!student.school_class) tempErrors.school_class = 'الصف مطلوب';
    
    if (student.is_bus_joined) {
      if (!student.bus) {
        tempErrors.bus = 'يرجى اختيار الباص';
      } else {
        // Check if selected bus is full
        const selectedBus = buses.find(bus => String(bus.id) === student.bus);
        if (selectedBus && selectedBus.student_count >= selectedBus.capacity) {
          tempErrors.bus = 'الباص المختار ممتلئ، يرجى اختيار باص آخر';
        }
      }
    }
  } else if (step === 2) {
    if (!student.parent_name) tempErrors.parent_name = 'اسم ولي الأمر مطلوب';
    else if (!isArabic(student.parent_name)) tempErrors.parent_name = ' يجب أن يكون باللغة العربية فقط';
    if (!student.parent_phone) tempErrors.parent_phone = 'رقم هاتف ولي الأمر مطلوب';
    else if (!isValidPhone(student.parent_phone))
      tempErrors.parent_phone = 'رقم الهاتف يجب أن يكون 10 أرقام ويبدأ بـ 0';

    // Validate optional second phone if provided
    if (student.parent_phone_2 && !isValidPhone(student.parent_phone_2))
      tempErrors.parent_phone_2 = 'رقم الهاتف الثاني يجب أن يكون 10 أرقام ويبدأ بـ 0';

    if (student.parent_email && !isValidEmail(student.parent_email))
      tempErrors.parent_email = 'البريد الإلكتروني غير صالح';
  }
  
  setErrors(tempErrors);
  return Object.keys(tempErrors).length === 0;
  };
  

  const handleNext = () => {
    setTriedNext(true);
    
    // Additional check for student ID validation in progress
    if (activeStep === 0 && (studentIdValidating || studentIdError)) {
      if (studentIdValidating) {
        showToast('جاري التحقق من رقم الهوية، يرجى الانتظار...', 'info');
        return;
      }
      if (studentIdError) {
        showToast('يرجى حل مشكلة رقم الهوية قبل المتابعة', 'error');
        return;
      }
    }
    
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
      setTriedNext(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  // Function to handle maximum students dialog close
  const handleMaxStudentsDialogClose = () => {
    setMaxStudentsDialog(false);
    navigate('/students');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (studentIdError) {
      showToast('لا يمكن حفظ الطالب: رقم الهوية مكرر', 'error');
      return;
    }
    
    if (validateStep(activeStep)) {
      if (activeStep === 4) { // Updated from 3 to 4
        setLoading(true);
      }
      const addressValue = student.address.trim() || 'فلسطين';

      // Create FormData for file upload
      const formData = new FormData();
      
      // Append all student data with correct field names
      formData.append('first_name', student.first_name || '');
      formData.append('second_name', student.second_name || '');
      formData.append('gender', student.gender || '');
      formData.append('birthdate', student.birthdate || '');
      formData.append('school_class', student.school_class || '');
      formData.append('parent_name', student.parent_name || '');
      formData.append('parent_phone_code', student.parent_phone_code || '972');
      formData.append('parent_phone', student.parent_phone || '');
      formData.append('parent_phone_2', student.parent_phone_2 || '');
      formData.append('parent_email', student.parent_email || '');
      formData.append('date_of_registration', student.date_of_registration || '');
      formData.append('parent_phone_2_code', student.parent_phone_2_code || '972');
      formData.append('note', student.note || '');
      formData.append('student_id', student.student_id || '');
      formData.append('is_bus_joined', student.is_bus_joined || false);
      formData.append('bus', student.bus || '');
      formData.append('address', addressValue);
      
      // Only append attachment if it exists
      if (student.attachment) {
        formData.append('attachment', student.attachment);
      }

      try {
        const createdStudent = await dispatch(createStudent(formData)).unwrap();
        showToast('تم حفظ الطالب بنجاح!', 'success');

        // Merge returned ID (and any other data) into your original student object
        const studentWithId = { ...student, id: createdStudent.id };

        navigate('/students/payment', { state: { student: studentWithId } });
      } catch (error) {
        console.error('Full Axios error:', error);
        console.error('Error data:', error);
      
        let errorMessage = 'فشل إنشاء الطالب، يرجى المحاولة مرة أخرى';
        let isMaxStudentsError = false;
      
        // ✅ Handle the actual error structure from your backend
        let data = null;
        
        // Check if error has response.data (typical Axios structure)
        if (error?.response?.data) {
          data = error.response.data;
          console.log('Found error.response.data:', data);
        } 
        // Check if error is the data itself (direct from Redux/thunk)
        else if (error && typeof error === 'object') {
          data = error;
          console.log('Using error as data directly:', data);
        }
        
        if (data) {
          console.log('Processing error data:', data);
          
          // ✅ NEW: Check for custom error format with arrays
          if (data.error_code && Array.isArray(data.error_code) && data.error_code.includes('MAX_STUDENTS_REACHED')) {
            console.log('Found MAX_STUDENTS_REACHED error code in array');
            isMaxStudentsError = true;
            
            // Extract error message from array
            if (data.error && Array.isArray(data.error) && data.error.length > 0) {
              errorMessage = data.error[0]; // Take first error message
            } else if (typeof data.error === 'string') {
              errorMessage = data.error;
            }
            
            // Store additional details if available
            setMaxStudentsDetails({
              maxStudents: data.max_students,
              currentCount: data.current_count,
              errorCode: data.error_code[0] || 'MAX_STUDENTS_REACHED'
            });
            
            console.log('Custom array error message:', errorMessage);
          } 
          // ✅ Check for custom error format with strings
          else if (data.error_code === 'MAX_STUDENTS_REACHED') {
            console.log('Found MAX_STUDENTS_REACHED error code as string');
            isMaxStudentsError = true;
            errorMessage = data.error;
            
            // Store additional details if available
            setMaxStudentsDetails({
              maxStudents: data.max_students,
              currentCount: data.current_count,
              errorCode: data.error_code
            });
            
            console.log('Custom string error message:', errorMessage);
          } else {
            // ✅ FALLBACK: Handle standard DRF format
            const messages = [];
        
            // Check for non_field_errors (like maximum students limit)
            if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
              console.log('Found non_field_errors:', data.non_field_errors);
              messages.push(...data.non_field_errors);
              
              // Check if it's a maximum students error - be more specific
              isMaxStudentsError = data.non_field_errors.some(msg => {
                const isMaxError = msg.includes('الحد الأقصى') || 
                                 msg.includes('المسموح') ||
                                 msg.includes('الوصول للحد الأقصى') ||
                                 msg.includes('maximum') ||
                                 msg.includes('limit');
                console.log('Checking message:', msg, 'Is max error:', isMaxError);
                return isMaxError;
              });
              
              console.log('Is maximum students error:', isMaxStudentsError);
            }
            
            // Check for other field errors
            for (const key in data) {
              if (key !== 'non_field_errors' && key !== 'error_code' && key !== 'error') {
                if (Array.isArray(data[key])) {
                  messages.push(...data[key]);
                } else if (typeof data[key] === 'string') {
                  messages.push(data[key]);
                }
              }
            }
        
            if (messages.length > 0) {
              errorMessage = messages.join(' / ');
            }
          }
        }
        
        console.log('Final errorMessage:', errorMessage);
        console.log('Will show dialog:', isMaxStudentsError);
        
        // Handle maximum students error specially
        if (isMaxStudentsError) {
          console.log('Setting max students dialog to true');
          setMaxStudentsMessage(errorMessage);
          setMaxStudentsDialog(true);
          console.log('Max students dialog state should be true now');
        } else {
          console.log('Showing toast error instead');
          showToast(errorMessage, 'error');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    navigate('/students');
  };

const isStepComplete = (step) => {
  if (step === 0) {
    return (
      !!student.first_name &&
      !!student.second_name &&
      !!student.gender &&
      !!student.birthdate &&
      isArabic(student.first_name) &&
      isArabic(student.second_name) &&
      (!student.student_id || (isValidId(student.student_id) && !studentIdError))
    );
  } else if (step === 1) {
    let busValidation = true;
    
    if (student.is_bus_joined) {
      if (!student.bus) {
        busValidation = false;
      } else {
        // Check if selected bus is full
        const selectedBus = buses.find(bus => String(bus.id) === student.bus);
        if (selectedBus && selectedBus.student_count >= selectedBus.capacity) {
          busValidation = false;
        }
      }
    }
    
    return !!student.school_class && busValidation;
  } else if (step === 2) {
    return (
      !!student.parent_name &&
      !!student.parent_phone &&
      isValidPhone(student.parent_phone) &&
      (!student.parent_phone_2 || isValidPhone(student.parent_phone_2)) &&
      (!student.parent_email || isValidEmail(student.parent_email))
    );
  } else if (step === 3) {
    // Notes step - always complete since notes are optional
    return true;
  } else if (step === 4) {
    // Attachments step - always complete since attachments are optional
    return true;
  }
  return false;
};

  const fieldSx = {
    fontFamily: 'Cairo, Tajawal, sans-serif',
    '& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormHelperText-root': {
      fontFamily: 'Cairo, Tajawal, sans-serif',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      transition: 'box-shadow 0.2s ease',
      '&:hover': { backgroundColor: 'rgba(67,56,202,0.02)' },
      '&.Mui-focused': {
        boxShadow: '0 0 0 3px rgba(67,56,202,0.12)',
        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2, borderColor: '#4338CA' },
      },
      '&.Mui-error': {
        '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(220,38,38,0.12)' },
      },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#4338CA' },
  };

  const StepCardHeader = ({ icon, title, subtitle, color, bg, borderColor }) => (
    <Box sx={{
      background: bg,
      px: 3, py: 2,
      display: 'flex', alignItems: 'center', gap: 1.5,
      borderBottom: `1px solid ${borderColor}`,
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: '12px',
        background: color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 4px 12px ${borderColor}`,
        flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          {subtitle}
        </Typography>
      </Box>
      <Box sx={{
        px: 1.5, py: 0.4, borderRadius: '8px',
        backgroundColor: borderColor,
        border: `1px solid ${borderColor}`,
      }}>
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          * مطلوب
        </Typography>
      </Box>
    </Box>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(67,56,202,0.07)', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            <StepCardHeader
              icon={<Person sx={{ color: '#fff', fontSize: 22 }} />}
              title="المعلومات الأساسية"
              subtitle="بيانات هوية الطالب الشخصية"
              color="linear-gradient(135deg, #4338CA, #5B21F4)"
              bg="linear-gradient(135deg, #EEF2FF 0%, #E0F2FE 100%)"
              borderColor="rgba(67,56,202,0.14)"
            />
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '16px' }}>
                <TextField
                  fullWidth
                  label="الاسم الأول *"
                  name="first_name"
                  value={student.first_name}
                  onChange={handleChange}
                  error={!!errors.first_name && triedNext}
                  helperText={(triedNext && errors.first_name) ? errors.first_name : ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  label="اسم العائلة *"
                  name="second_name"
                  value={student.second_name}
                  onChange={handleChange}
                  error={!!errors.second_name && triedNext}
                  helperText={(triedNext && errors.second_name) ? errors.second_name : ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  label="رقم الهوية (اختياري)"
                  name="student_id"
                  value={student.student_id}
                  onChange={handleChange}
                  onBlur={(e) => handleStudentIdValidation(e.target.value)}
                  error={!!errors.student_id || !!studentIdError}
                  helperText={errors.student_id || studentIdError || '9 أرقام'}
                  InputProps={{
                    sx: { borderRadius: '10px' },
                    startAdornment: <Badge color="action" sx={{ mr: 1 }} />,
                    endAdornment: studentIdValidating && <CircularProgress size={20} />,
                  }}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  type="date"
                  label="تاريخ الميلاد *"
                  name="birthdate"
                  value={student.birthdate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.birthdate}
                  helperText={errors.birthdate || ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  select
                  label="الجنس *"
                  name="gender"
                  value={student.gender}
                  onChange={handleChange}
                  error={!!errors.gender}
                  helperText={errors.gender || ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 250 } } } }}
                >
                  <MenuItem value="ذكر">ذكر</MenuItem>
                  <MenuItem value="أنثى">أنثى</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  label="العنوان"
                  name="address"
                  value={student.address}
                  onChange={handleChange}
                  placeholder="فلسطين"
                  helperText=" "
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                />
              </Box>
            </CardContent>
          </Card>
        );
      case 1:
        return (
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(67,56,202,0.07)', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            <StepCardHeader
              icon={<School sx={{ color: '#fff', fontSize: 22 }} />}
              title="المعلومات الأكاديمية"
              subtitle="الصف الدراسي وتاريخ التسجيل"
              color="linear-gradient(135deg, #16A34A, #22C55E)"
              bg="linear-gradient(135deg, #DCFCE7 0%, #ECFDF5 100%)"
              borderColor="rgba(22,163,74,0.14)"
            />
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '16px' }}>
                <TextField
                  fullWidth
                  select
                  label="الصف *"
                  name="school_class"
                  value={student.school_class}
                  onChange={handleChange}
                  error={!!errors.school_class}
                  helperText={errors.school_class || ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 250 } } } }}
                >
                  {classesStatus === 'loading' ? (
                    <MenuItem disabled>جاري التحميل...</MenuItem>
                  ) : classesStatus === 'failed' ? (
                    <MenuItem disabled>فشل تحميل الصفوف</MenuItem>
                  ) : classes.length === 0 ? (
                    <MenuItem disabled>لا توجد صفوف متاحة</MenuItem>
                  ) : (
                    classes.map((cls) => (
                      <MenuItem key={cls.id} value={String(cls.id)}>{cls.name}</MenuItem>
                    ))
                  )}
                </TextField>
                <TextField
                  fullWidth
                  type="date"
                  label="تاريخ التسجيل"
                  name="date_of_registration"
                  value={student.date_of_registration}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  helperText=" "
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                />
                {/* Bus checkbox — spans full width */}
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={student.is_bus_joined}
                        onChange={(e) => setStudent({ ...student, is_bus_joined: e.target.checked, bus: e.target.checked ? student.bus : '' })}
                        name="is_bus_joined"
                        color="success"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الالتحاق بباص المدرسة</Typography>
                        {student.is_bus_joined && (
                          <Box sx={{ px: 1, py: 0.25, borderRadius: 1, backgroundColor: '#16A34A', color: '#fff' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>مفعّل</Typography>
                          </Box>
                        )}
                      </Box>
                    }
                    sx={{
                      border: student.is_bus_joined ? '1.5px solid #16A34A' : '1px solid #E5E7EB',
                      borderRadius: 2, p: 1.5, width: '100%', m: 0,
                      backgroundColor: student.is_bus_joined ? 'rgba(22,163,74,0.05)' : 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: 'rgba(22,163,74,0.08)', borderColor: '#22C55E' },
                    }}
                  />
                </Box>
                {student.is_bus_joined && (
                  <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                      fullWidth
                      select
                      label="اختر الباص *"
                      name="bus"
                      value={student.bus}
                      onChange={handleChange}
                      error={!!errors.bus}
                      helperText={errors.bus || ' '}
                      InputProps={{ sx: { borderRadius: '10px' } }}
                      sx={fieldSx}
                      SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 250 } } } }}
                    >
                      {busesStatus === 'loading' ? (
                        <MenuItem disabled>جاري التحميل...</MenuItem>
                      ) : busesStatus === 'failed' ? (
                        <MenuItem disabled>فشل تحميل الباصات</MenuItem>
                      ) : buses.length === 0 ? (
                        <MenuItem disabled>لا توجد باصات متاحة</MenuItem>
                      ) : (
                        buses.map((bus) => {
                          const isFull = bus.student_count >= bus.capacity;
                          const availableSeats = bus.capacity - bus.student_count;
                          return (
                            <MenuItem key={bus.id} value={String(bus.id)} disabled={isFull}
                              sx={{
                                backgroundColor: isFull ? 'rgba(244,67,54,0.1)' : 'inherit',
                                '&:hover': { backgroundColor: isFull ? 'rgba(244,67,54,0.15)' : 'rgba(0,0,0,0.04)' },
                                '&.Mui-disabled': { color: 'rgba(244,67,54,0.7)' },
                              }}>
                              <Box sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: isFull ? 'bold' : 'normal' }}>
                                  رقم: {bus.bus_number} | السائق: {bus.manager_name || bus.driver_name || 'غير محدد'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: isFull ? 'error.main' : 'text.secondary', display: 'block' }}>
                                  {isFull ? `الباص ممتلئ (${bus.student_count}/${bus.capacity})` : `مقاعد متاحة: ${availableSeats} من ${bus.capacity}`}
                                </Typography>
                              </Box>
                            </MenuItem>
                          );
                        })
                      )}
                    </TextField>
                    {student.bus && (() => {
                      const selectedBus = buses.find(b => String(b.id) === student.bus);
                      return selectedBus && selectedBus.student_count >= selectedBus.capacity ? (
                        <Box sx={{ p: 2, backgroundColor: 'rgba(244,67,54,0.08)', borderRadius: 1, border: '1px solid rgba(244,67,54,0.25)' }}>
                          <Typography variant="body2" color="error" sx={{ fontWeight: 'bold', mb: 0.5 }}>⚠️ تحذير: الباص المختار ممتلئ</Typography>
                          <Typography variant="caption" color="error">
                            هذا الباص وصل للحد الأقصى ({selectedBus.student_count}/{selectedBus.capacity}). يرجى اختيار باص آخر.
                          </Typography>
                        </Box>
                      ) : null;
                    })()}
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        );
      case 2:
        return (
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(67,56,202,0.07)', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            <StepCardHeader
              icon={<Contacts sx={{ color: '#fff', fontSize: 22 }} />}
              title="بيانات ولي الأمر"
              subtitle="معلومات التواصل مع ولي الأمر"
              color="linear-gradient(135deg, #D97706, #F59E0B)"
              bg="linear-gradient(135deg, #FEF3C7 0%, #FFFBEB 100%)"
              borderColor="rgba(217,119,6,0.14)"
            />
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '16px' }}>
                {/* Parent name — full width */}
                <TextField
                  fullWidth
                  label="اسم ولي الأمر *"
                  name="parent_name"
                  value={student.parent_name}
                  onChange={handleChange}
                  error={!!errors.parent_name && triedNext}
                  helperText={(triedNext && errors.parent_name) ? errors.parent_name : ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={{ gridColumn: '1 / -1', ...fieldSx }}
                />

                {/* Phone 1 */}
                <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
                  <TextField
                    select
                    label="رمز"
                    name="parent_phone_code"
                    value={student.parent_phone_code}
                    onChange={handleChange}
                    helperText=" "
                    sx={{ width: 100, flexShrink: 0, ...fieldSx }}
                    InputProps={{ sx: { borderRadius: '10px' } }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { minWidth: 130 } } } }}
                  >
                    {PHONE_CODE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{option.flag}</span>
                          <span style={{ fontSize: '0.8rem' }}>{option.label}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="هاتف ولي الأمر *"
                    name="parent_phone"
                    value={student.parent_phone}
                    onChange={handleChange}
                    error={!!errors.parent_phone}
                    helperText={errors.parent_phone || ' '}
                    InputProps={{
                      sx: { borderRadius: '10px' },
                      startAdornment: <Phone color="action" sx={{ mr: 0.5 }} />,
                    }}
                    sx={{ flex: 1, minWidth: 0, ...fieldSx }}
                    placeholder="0501234567"
                  />
                </Box>

                {/* Phone 2 */}
                <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
                  <TextField
                    select
                    label="رمز"
                    name="parent_phone_2_code"
                    value={student.parent_phone_2_code}
                    onChange={handleChange}
                    helperText=" "
                    sx={{ width: 100, flexShrink: 0, ...fieldSx }}
                    InputProps={{ sx: { borderRadius: '10px' } }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { minWidth: 130 } } } }}
                  >
                    {PHONE_CODE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{option.flag}</span>
                          <span style={{ fontSize: '0.8rem' }}>{option.label}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="هاتف ولي الأمر الثاني (اختياري)"
                    name="parent_phone_2"
                    value={student.parent_phone_2}
                    onChange={handleChange}
                    error={!!errors.parent_phone_2}
                    helperText={errors.parent_phone_2 || ' '}
                    InputProps={{
                      sx: { borderRadius: '10px' },
                      startAdornment: <Phone color="action" sx={{ mr: 0.5 }} />,
                    }}
                    sx={{ flex: 1, minWidth: 0, ...fieldSx }}
                    placeholder="0501234567"
                  />
                </Box>

                {/* Email — full width */}
                <TextField
                  fullWidth
                  label="البريد الإلكتروني (اختياري)"
                  name="parent_email"
                  value={student.parent_email}
                  onChange={handleChange}
                  type="email"
                  error={!!errors.parent_email}
                  helperText={errors.parent_email ? `${errors.parent_email} (مثال: example@email.com)` : ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={{ gridColumn: '1 / -1', ...fieldSx }}
                />
              </Box>
            </CardContent>
          </Card>
        );
      case 3:
        return (
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(67,56,202,0.07)', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            <StepCardHeader
              icon={<NoteAdd sx={{ color: '#fff', fontSize: 22 }} />}
              title="ملاحظات"
              subtitle="أي معلومات إضافية عن الطالب (اختياري)"
              color="linear-gradient(135deg, #5B21F4, #7C3AED)"
              bg="linear-gradient(135deg, #EDE9FE 0%, #F5F3FF 100%)"
              borderColor="rgba(91,33,244,0.14)"
            />
            <CardContent sx={{ p: 3 }}>
              <TextField
                fullWidth
                label="ملاحظات"
                name="note"
                value={student.note}
                onChange={handleChange}
                multiline
                rows={7}
                InputProps={{ sx: { borderRadius: '10px' } }}
                sx={fieldSx}
                placeholder="أضف أي ملاحظات خاصة بالطالب هنا..."
              />
            </CardContent>
          </Card>
        );
      case 4:
        return (
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(67,56,202,0.07)', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            <StepCardHeader
              icon={<AttachFile sx={{ color: '#fff', fontSize: 22 }} />}
              title="المرفقات"
              subtitle="الوثائق والمستندات الداعمة (اختياري)"
              color="linear-gradient(135deg, #0284C7, #00BFFF)"
              bg="linear-gradient(135deg, #E0F2FE 0%, #F0F9FF 100%)"
              borderColor="rgba(2,132,199,0.14)"
            />
            <CardContent sx={{ p: 3 }}>
              <Box sx={{
                border: '2px dashed #0284C7',
                borderRadius: 3,
                p: 4,
                textAlign: 'center',
                backgroundColor: 'rgba(2,132,199,0.03)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(2,132,199,0.07)',
                  borderColor: '#0369A1'
                }
              }}>
                    <input
                      accept="image/*,application/pdf,.doc,.docx"
                      style={{ display: 'none' }}
                      id="attachment-button"
                      type="file"
                      name="attachment"
                      onChange={handleChange}
                    />
                    <label htmlFor="attachment-button">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<AttachFile />}
                        sx={{
                          mb: 2,
                          borderRadius: 2.5,
                          textTransform: 'none',
                          fontWeight: 700,
                          fontFamily: 'Cairo, Tajawal, sans-serif',
                          borderColor: '#0284C7',
                          color: '#0284C7',
                          '&:hover': {
                            borderColor: '#0369A1',
                            backgroundColor: 'rgba(2,132,199,0.08)'
                          }
                        }}
                      >
                        إضافة مرفق
                      </Button>
                    </label>
                    <Typography variant="body2" sx={{ mb: 2, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      يمكن رفع الصور، ملفات PDF، أو مستندات Word
                    </Typography>
                    {student.attachmentPreview && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>معاينة المرفق:</Typography>
                        {student.attachment?.type?.startsWith('image/') ? (
                          <Avatar
                            src={student.attachmentPreview}
                            alt="معاينة المرفق"
                            variant="rounded"
                            sx={{ width: 100, height: 100, mx: 'auto', boxShadow: 2 }}
                          />
                        ) : (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: 1,
                            p: 2,
                            backgroundColor: 'rgba(0,0,0,0.05)',
                            borderRadius: 1
                          }}>
                            <AttachFile color="primary" />
                            <Typography variant="body2">
                              تم تحميل: {student.attachment?.name}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
              </Box>
            </CardContent>
          </Card>
        );
      default:
        return 'خطأ غير معروف';
    }
  };

  // Maximum Students Dialog Component
  const MaxStudentsDialog = () => {
    console.log('MaxStudentsDialog rendering - open:', maxStudentsDialog, 'message:', maxStudentsMessage);
    
    return (
      <Dialog
        open={maxStudentsDialog}
        onClose={handleMaxStudentsDialogClose}
        maxWidth="sm"
        fullWidth
        dir="rtl"
        style={{ zIndex: 9999 }} // Ensure it's on top
        PaperProps={{ sx: { borderRadius: 3, fontFamily: 'Cairo, Tajawal, sans-serif', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{
          backgroundColor: '#FEF3C7',
          color: '#D97706',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: 800,
          fontFamily: 'Cairo, Tajawal, sans-serif',
        }}>
          <Warning />
          تم الوصول للحد الأقصى من الطلاب
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
            <AlertTitle sx={{ fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>لا يمكن إضافة طلاب جدد</AlertTitle>
            {maxStudentsMessage}
          </Alert>


          <Typography variant="body2" sx={{ mt: 2, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
            للمتابعة في إضافة طلاب جدد، يرجى التواصل مع الإدارة لزيادة الحد المسموح من الطلاب.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleMaxStudentsDialogClose}
            variant="contained"
            sx={{
              borderRadius: 2.5,
              px: 3,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              bgcolor: '#4338CA',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
            }}
          >
            العودة إلى قائمة الطلاب
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const progressPercent = Math.round(((activeStep) / steps.length) * 100);

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <Box sx={{ maxWidth: 920, mx: 'auto' }}>
        {/* Hero Header */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            borderRadius: '24px 24px 0 0',
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            p: { xs: 2.5, sm: '28px 36px' },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* decorative shapes */}
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ position: 'absolute', bottom: -60, left: -30, width: 180, height: 180, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.06)' }} />
          <Box sx={{ position: 'absolute', top: 10, left: '40%', width: 80, height: 80, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.05)' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', gap: 2 }}>
            {/* Back button */}
            <Tooltip title="العودة إلى قائمة الطلاب">
              <IconButton
                onClick={handleCancel}
                sx={{
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  backdropFilter: 'blur(6px)',
                  flexShrink: 0,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.5)' },
                }}
              >
                <ArrowBack />
              </IconButton>
            </Tooltip>

            {/* Title center */}
            <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: '12px',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backdropFilter: 'blur(4px)',
                }}>
                  <School sx={{ color: '#fff', fontSize: 24 }} />
                </Box>
                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, letterSpacing: 0.3, fontFamily: 'Cairo, Tajawal, sans-serif', fontSize: { xs: '1.4rem', sm: '1.75rem' } }}>
                  تسجيل طالب جديد
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                {steps[activeStep]} — الخطوة {activeStep + 1} من {steps.length}
              </Typography>
            </Box>

            {/* Step counter pill */}
            <Box sx={{
              textAlign: 'center', flexShrink: 0,
              backgroundColor: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: '12px', px: 2, py: 0.75,
              backdropFilter: 'blur(6px)',
            }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block', lineHeight: 1.1, fontSize: '0.68rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                الاكتمال
              </Typography>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.2, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                {progressPercent}%
              </Typography>
            </Box>
          </Box>
        </MotionPaper>

        {/* Animated progress bar */}
        <Box sx={{ height: 5, background: 'rgba(67,56,202,0.12)' }}>
          <Box sx={{
            height: '100%',
            width: `${progressPercent}%`,
            background: 'linear-gradient(90deg, #00BFFF 0%, #5B21F4 100%)',
            transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
            borderRadius: '0 3px 3px 0',
            boxShadow: '0 0 8px rgba(91,33,244,0.5)',
          }} />
        </Box>

        {/* Main card */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            borderRadius: '0 0 24px 24px',
            p: { xs: 2, sm: 4 },
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            borderTop: 'none',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          }}
        >
          {/* Stepper */}
          <Box sx={{
            backgroundColor: '#F8F9FF',
            borderRadius: 3,
            pt: 2.5,
            pb: 1.5,
            px: 1,
            mb: 4,
            border: '1px solid #E5E7EB',
          }}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              connector={<ColorlibConnector />}
            >
              {steps.map((label, index) => (
                <Step key={label} completed={activeStep > index}>
                  <StepLabel StepIconComponent={ColorlibStepIcon}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: activeStep === index ? 700 : 500,
                        fontSize: activeStep === index ? '0.8rem' : '0.72rem',
                        fontFamily: 'Cairo, Tajawal, sans-serif',
                        color: activeStep === index
                          ? '#4338CA'
                          : activeStep > index
                            ? '#16A34A'
                            : '#9CA3AF',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {label}
                    </Typography>
                    {activeStep > index && (
                      <Typography variant="caption" sx={{ color: '#16A34A', fontSize: '0.65rem', display: 'block', mt: -0.3, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        ✓ مكتمل
                      </Typography>
                    )}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <form onSubmit={handleSubmit}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
                transition={{ duration: 0.3 }}
              >
                {getStepContent(activeStep)}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 4,
              pt: 3,
              borderTop: '1px solid #E5E7EB',
            }}>
              {/* Back */}
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={activeStep === 0 || loading}
                startIcon={<KeyboardArrowRight />}
                sx={{
                  borderRadius: 2.5,
                  px: 3, py: 1,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#C7D2FE',
                  color: '#4338CA',
                  borderWidth: '1.5px',
                  '&:hover': { borderWidth: '1.5px', borderColor: '#4338CA', backgroundColor: '#EEF2FF' },
                  '&.Mui-disabled': { borderStyle: 'dashed' },
                }}
              >
                العودة
              </Button>

              {/* Step pills */}
              <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center' }}>
                {steps.map((_, idx) => (
                  <Box key={idx} sx={{
                    width: idx === activeStep ? 28 : 8,
                    height: 8,
                    borderRadius: 10,
                    backgroundColor: idx < activeStep
                      ? '#16A34A'
                      : idx === activeStep
                        ? '#4338CA'
                        : '#E5E7EB',
                    transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                    boxShadow: idx === activeStep ? '0 0 6px rgba(67,56,202,0.5)' : 'none',
                  }} />
                ))}
              </Box>

              {/* Next / Submit */}
              <Box>
                {activeStep < steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={loading || (activeStep === 0 && (studentIdValidating || !!studentIdError))}
                    endIcon={studentIdValidating ? <CircularProgress size={16} color="inherit" /> : <KeyboardArrowLeft />}
                    sx={{
                      borderRadius: 2.5,
                      px: 3.5, py: 1,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      bgcolor: '#4338CA',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#3730A3',
                        boxShadow: 'none',
                      },
                      '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                    }}
                  >
                    {studentIdValidating ? 'جاري التحقق...' : 'التالي'}
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleCancel}
                      disabled={loading}
                      startIcon={<Cancel />}
                      sx={{
                        borderRadius: 2.5, px: 2.5, py: 1,
                        fontWeight: 700, borderWidth: '1.5px',
                        textTransform: 'none',
                        fontFamily: 'Cairo, Tajawal, sans-serif',
                        borderColor: '#DC2626', color: '#DC2626',
                        '&:hover': { borderWidth: '1.5px', borderColor: '#DC2626', backgroundColor: '#FEE2E2' },
                      }}
                    >
                      إلغاء
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={loading || !isStepComplete(activeStep) || !!studentIdError}
                      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Save />}
                      sx={{
                        borderRadius: 2.5, px: 3.5, py: 1,
                        fontWeight: 700,
                        textTransform: 'none',
                        fontFamily: 'Cairo, Tajawal, sans-serif',
                        bgcolor: '#4338CA',
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: '#3730A3',
                          boxShadow: 'none',
                        },
                        '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                      }}
                    >
                      {loading ? 'جارٍ الحفظ...' : 'حفظ الطالب'}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </form>

          {/* Maximum Students Dialog */}
          <MaxStudentsDialog />
        </MotionPaper>
      </Box>
    </motion.div>
    </Box>
  );
};

export default CreateStudentPage;